<script setup>
import Breadcrumb from "primevue/breadcrumb";
import {computed, defineAsyncComponent, onMounted, ref} from "vue";
import Button from "primevue/button";
import {Link, usePage} from "@inertiajs/vue3";
import {useNeoStore} from "../../../../state/neo.js";
import Avatar from "primevue/avatar";
import {getInitials} from "../../../../helpers.js";

const props = defineProps({
    obj: Object,
    closeRoute: String,
    isSettings: Boolean,
    isModal: Boolean,
    flags: Array
})

const neoStore = useNeoStore();
const page = usePage();
const breadcrumbs = computed(() => {
    return props.obj.__breadcrumbs.map((crumb) => {
        return {
            label: crumb
        }
    });
})

const components = import.meta.glob('../../../administration/views/**/*.vue');

const component = defineAsyncComponent(() => {
    const pageParts = props.obj.__component?.split('/');
    const key = Object.keys(components).find(k => k.endsWith(`${pageParts[pageParts.length-1]}.vue`));
    if (key) return components[key]();
    throw new Error('Component not found: ' + page.component);
});

const hasFlag = (flag) => {
    let flags = Array.isArray(props.flags) ? props.flags : Object.values(props.flags);
    return flags?.includes(flag);
}
</script>

<template>
    <div class="flex flex-col bg-white dark:bg-gray-800 dark:border-neutral-700 w-full" :class="[isSettings || isModal ? '' : 'border-l', isModal ? 'h-full min-h-0' : 'md:h-dvh']">
        <div id="neo-aside-header"
            class=" pt-4 md:pt-8 px-4 pb-4 relative"
            :class="isSettings ? '' : 'bg-white max-md:sticky max-md:top-0 z-10 dark:bg-neutral-900 dark:border-neutral-700 border-b '"
        >
            <Breadcrumb v-if="!hasFlag('use_avatar')" :model="breadcrumbs" class="!bg-transparent"></Breadcrumb>
            <div class="flex items-center ml-3 mr-8 gap-3 mb-2" :class="hasFlag('use_avatar') ? 'mt-5' : '-mt-2'">
                <Link v-if="closeRoute && !isModal" :href="closeRoute" :class="hasFlag('use_avatar') ? 'absolute z-40 top-4 left-6' : ''">
                    <Button size="small" rounded severity="secondary" icon="pi pi-arrow-left"></Button>
                </Link>
                <Avatar v-if="hasFlag('use_avatar')" size="xlarge" class="mr-2" shape="circle" :label="getInitials(obj.__title)"></Avatar>
                <Transition name="slide" mode="out-in">
                    <h3
                        :key="obj.__title"
                        :class="isSettings ? 'md:hidden' : ''"
                        class="text-3xl font-bold"
                    >
                        {{ obj.__title }}
                    </h3>
                </Transition>
                <div class="grow"></div>
                <div id="neo-aside-header-actions" class="flex items-center gap-2"></div>
                <Button v-if="!isModal" size="small" @click="neoStore.toggleAside" class="max-md:!hidden" rounded severity="secondary"
                        :icon="neoStore.asideExpanded ? 'pi pi-arrow-down-left-and-arrow-up-right-to-center':'pi pi-arrow-up-right-and-arrow-down-left-from-center'"></Button>
                <Link v-if="closeRoute && isModal" :href="closeRoute" class="absolute z-40 top-4 right-6">
                    <Button size="small" rounded severity="secondary" icon="pi pi-times"></Button>
                </Link>
            </div>
        </div>
        <div
            id="neo-aside-content"
            class="overflow-x-hidden"
            :class="isModal ? 'flex-1 min-h-0 overflow-y-auto' : 'p-3 md:p-4 lg:overflow-y-auto'"
        >

                <Transition name="list-page" mode="out-in">
                    <component
                        v-if="component"
                        :is="component"
                        v-bind="obj.__data"
                        :key="component"
                    />
                </Transition>
        </div>

    </div>
</template>

<style scoped lang="scss">
.fade-enter-active,
.fade-leave-active {
    transition: opacity .8s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}

.slide-enter-active,
.slide-leave-active {
    transition: all .25s ease;
    position: relative;
}

.slide-enter-from {
    opacity: 0;
    top: 10px;
}

.slide-leave-to {
    opacity: 0;
    top: -10px;
}
</style>
